import { AvailabilityStatus, AvailabilityStatusPresentation, AvailabilityStatusType } from '@dustin-web/microsite-data/src/graphql/product/generated/graphql-types';
import type { Translator } from '@eo-locale/core'
import type { GenericAvailabilityType } from "../../cards";
import { type AvailabilityTypeKeys, type TranslationStockMapStatesValues, AvailabilityTypes, AvailabilityStatusIconColor } from './types';
import { STOCK_TRANSLATIONS_MAP } from './translations';

function isObject(val: unknown) {
  return typeof val === "object" && val !== null && !Array.isArray(val);
}

function getStockQuantity(
  availability: GenericAvailabilityType,
  availabilityType: AvailabilityTypeKeys,
  isBid: boolean,
) {
  const {
    internalStock,
    externalStock,
    internalEtaStock,
    availabilityDetails,
    showAvailabilityQuantities
  } = availability ?? {}

  const internalQuantity = internalStock?.quantity || 0
  const externalQuantity = externalStock?.quantity || 0
  const internalEtaQuantity = internalEtaStock?.quantity || 0
  const internalEta = availabilityDetails?.formattedDateUtc

  const totalQuantity = internalQuantity + externalQuantity + internalEtaQuantity

  const isEstimatedInStore =
    availabilityType === AvailabilityTypes.ESTIMATED_IN_STORE_BY_DATE && internalEtaQuantity
    && internalEtaQuantity
    && internalEta

  const isImmediateShipping =
    availabilityType === AvailabilityTypes.IN_STOCK_IMMEDIATE_SHIPPING && internalQuantity

  let quantity: string | number = 0

  if (showAvailabilityQuantities && !isBid) {
    quantity = totalQuantity
  } else if (isEstimatedInStore) {
    quantity = internalEtaQuantity
  } else if (isImmediateShipping) {
    quantity = internalQuantity
  } else {
    quantity = totalQuantity > 0 ? totalQuantity : isBid ? externalQuantity : 0
  }

  if (quantity <= 0) {
    quantity = 0
  }

  if (quantity > 100) {
    quantity = '+100'
  }

  return {
    internalEta,
    quantity: `${quantity}`
  }
}

function getStockIconColor(
  availabilityStatusPresentation?: string,
  availabilityStatus?: string,
  availabilityDetail?: string,
  isBid?: boolean,
  isNL?: boolean
): AvailabilityStatusIconColor {
  if (
    (isNL && availabilityDetail === AvailabilityStatusType.ProductDistributorLeadTimeOneTwoDays) ||
    availabilityStatusPresentation === AvailabilityStatusPresentation.ShowAsImmediateShipping ||
    (availabilityStatus === AvailabilityStatus.ExternalStock && isBid)
  ) {
    return AvailabilityStatusIconColor.Green
  }

  switch (availabilityStatusPresentation) {
    case AvailabilityStatusPresentation.ShowAsExternalStock:
      return AvailabilityStatusIconColor.Orange
    case AvailabilityStatusPresentation.ShowAsComingSoon:
      return AvailabilityStatusIconColor.Gray
    default:
      return AvailabilityStatusIconColor.White
  }
}

/**
 * Returns translated stock availability information including text, tooltip, and icon color.
 *
 * @param translate - The translation function used to resolve localized strings.
 * @param availability - The stock availability object containing quantity, ETA, and status data.
 * @param availabilityType - The key used to select the appropriate translation entry from `STOCK_TRANSLATIONS_MAP`.
 * @param isBid - Whether the stock item belongs to a bid; if true, adds a bid-specific suffix.
 * @param isNL - Optional flag indicating if Netherlands-specific logic should apply (affects icon color rendering).
 *
 * @returns An object containing:
 * - `iconColor`: The color code representing stock status.
 * - `tooltip`: An optional localized tooltip text used to display additional stock details.
 * - `translation`: A localized string describing current stock availability, including quantity and ETA when applicable.
 *
 * @remarks
 * The function determines the stock state by querying `getStockQuantity()` and selecting the appropriate 
 * translation key from `STOCK_TRANSLATIONS_MAP`. It handles multiple translation scenarios:
 * - Uses quantity-based translations when stock numbers are available.
 * - Includes ETA (expected arrival date) information when present.
 * - Appends quantity info to the translated message if configured to show quantities.
 * - Adds a bid-specific suffix when `isBid` is true.
 *
 * If no valid translation key is found for the given availability type, the function returns undefined.
 */
export function getStockAvailability(
  translate: Translator['translate'],
  availability: GenericAvailabilityType,
  availabilityType: AvailabilityTypeKeys,
  isBid: boolean,
  isNL?: boolean
) {

  if (!availability) {
    return
  }

  const { internalEta, quantity } = getStockQuantity(availability, availabilityType, isBid);
  const { availabilityStatusPresentation, availabilityStatus, availabilityDetails } = availability ?? {}

  const key = availabilityType as keyof typeof STOCK_TRANSLATIONS_MAP;
  const translationKey: string | TranslationStockMapStatesValues = STOCK_TRANSLATIONS_MAP[key] ?? ''
  const translationHasStates = isObject(translationKey)

  if (!translationKey) {
    return
  }

  const iconColor = getStockIconColor(
    availabilityStatusPresentation,
    availabilityStatus,
    availabilityDetails?.key,
    isBid,
    isNL
  )

  let translation: string;
  let tooltip: string | undefined;

  if (translationHasStates) {
    const translations = translationKey as TranslationStockMapStatesValues

    const {
      default: defaultTranslationKey,
      quantityLeft: quantityTranslationKey,
      tooltip: tooltipTranslationKey
    } = translations ?? {}

    translation = parseInt(quantity) > 0 && quantityTranslationKey
      ? translate(quantityTranslationKey, { quantity })
      : translate(defaultTranslationKey)

    const tooltiplTranslation = tooltipTranslationKey && translate(tooltipTranslationKey)

    tooltip = !tooltiplTranslation?.includes('_') ? tooltiplTranslation : undefined

  } else {
    translation = translate(translationKey as string, {
      quantity,
      date: internalEta,
    })
  }

  // If availability quantities should be shown and there’s stock available,
  // append the quantity info to the translated message (only if not already included)
  if (availability.showAvailabilityQuantities
    && parseInt(quantity) > 0
    && !translation.includes(quantity)
  ) {
    translation = `${translation} (${translate(STOCK_TRANSLATIONS_MAP.QUANTITY_LEFT as string, { quantity })}) `
  }

  if (isBid) {
    translation = `${translation} ${translate(STOCK_TRANSLATIONS_MAP.BID_SUFFIX as string)}`
  }

  return { iconColor, tooltip, translation, }
}
